import React, { useEffect, useState } from 'react';
import { collection, getDocs, query, where } from 'firebase/firestore';
import { db } from '../../firebase';
import { useAuth } from '../../contexts/AuthContext';
import DashboardLayout from '../../components/DashboardLayout';
import CohortSelector from '../../components/CohortSelector';
import useCohort from '../../hooks/useCohort';
import CoachPerformance from '../../components/CoachPerformance';

const STAGE_COLORS = {
  'Problem-Solution Fit':        '#e8522a',
  'Product-Market Fit':          '#f4a261',
  'Early Growth & Monetization': '#4a7c3f',
  'Scaling & Regional Expansion':'#2a9d8f',
};

export default function CoachDashboard() {
  const { profile } = useAuth();
  const { cohorts, selectedCohort, setSelectedCohort } = useCohort(profile);
  const [companies, setCompanies]           = useState([]);
  const [pendingAssessments, setPending]    = useState([]);
  const [loading, setLoading]               = useState(true);

  useEffect(() => {
    if (!profile?.uid) return;
    async function load() {
      try {
        // Load assigned companies
        const q = query(collection(db, 'companies'), where('coachId', '==', profile.uid));
        const snap = await getDocs(q);
        const comps = snap.docs.map(d => ({ id: d.id, ...d.data() }));
        setCompanies(comps);

        // Check for pending (submitted but not validated) periodic assessments
        const pending = [];
        for (const comp of comps) {
          const aSnap = await getDocs(
            query(
              collection(db, 'companies', comp.id, 'assessments'),
              where('type', '==', 'periodic'),
              where('status', '==', 'submitted')
            )
          );
          if (!aSnap.empty) {
            aSnap.docs.forEach(d => pending.push({
              ...d.data(), assessmentDocId: d.id,
              companyId: comp.id, companyName: comp.name
            }));
          }
        }
        setPending(pending);
      } catch (err) { console.error(err); }
      finally { setLoading(false); }
    }
    load();
  }, [profile]);

  return (
    <DashboardLayout>
      <div className="gm-page">
        <div className="gm-page-header">
          <div>
            <h1 className="gm-page-title">My Companies</h1>
            <p className="gm-page-sub">Welcome back, {profile?.displayName}</p>
            <CohortSelector cohorts={cohorts} selectedCohort={selectedCohort} onSelect={setSelectedCohort} />
          </div>
        </div>

        {loading ? (
          <div className="gm-loading"><div className="gm-spinner" /></div>
        ) : (
          <>
            {/* Pending validation banner */}
            {pendingAssessments.length > 0 && (
              <div className="gm-validation-banner">
                <div className="gm-validation-banner-icon">🔔</div>
                <div className="gm-validation-banner-content">
                  <div className="gm-validation-banner-title">
                    {pendingAssessments.length} assessment{pendingAssessments.length > 1 ? 's' : ''} awaiting your validation
                  </div>
                  <div className="gm-validation-banner-items">
                    {pendingAssessments.map((a, idx) => (
                      <a key={idx} href={`/scaling/coach/company/${a.companyId}`}
                        className="gm-validation-banner-link">
                        {a.companyName} →
                      </a>
                    ))}
                  </div>
                </div>
              </div>
            )}

            {/* My performance */}
            <div className="gm-section">
              <h2 className="gm-section-title">My performance</h2>
              <CoachPerformance coachId={profile?.uid} coachName={profile?.displayName} showAI={true} />
            </div>

            {/* Company cards */}
            <div className="gm-company-grid">
              {companies.map(c => (
                <div key={c.id} className="gm-company-card">
                  <div className="gm-company-card-header">
                    <div className="gm-company-card-name">{c.name}</div>
                    <span className="gm-stage-pill"
                      style={{ background: (STAGE_COLORS[c.currentStage]||'#ccc')+'22', color: STAGE_COLORS[c.currentStage]||'#666' }}>
                      {c.currentStage}
                    </span>
                  </div>
                  <p className="gm-company-card-desc">{c.description}</p>

                  {/* Stage progress */}
                  <div className="gm-score-track-label">
                    <span style={{ color: STAGE_COLORS[c.currentStage] }}>
                      {c.currentStageUpgrade} · {c.currentStageProgress}% through stage
                    </span>
                    <span style={{ color: '#94a3b8', fontSize: '0.75rem' }}>Target: {c.targetStage}</span>
                  </div>
                  <div className="gm-score-track">
                    <div className="gm-score-track-target"
                      style={{ width: `${((c.targetScore||0)/160)*100}%` }} />
                    <div className="gm-score-track-current"
                      style={{ width: `${((c.currentScore||0)/160)*100}%`, background: STAGE_COLORS[c.currentStage]||'#4a5481' }} />
                  </div>

                  {/* Pending badge */}
                  {pendingAssessments.some(a => a.companyId === c.id) && (
                    <div className="gm-pending-badge">🔔 Assessment pending validation</div>
                  )}

                  <div className="gm-company-card-actions">
                    <a href={`/scaling/coach/company/${c.id}`} className="gm-btn gm-btn-sm gm-btn-primary">
                      View & log sessions →
                    </a>
                  </div>
                </div>
              ))}
            </div>
          </>
        )}
      </div>
    </DashboardLayout>
  );
}
